import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from 'src/app/data.service';

/**
 *  ADATBANK – szabad keresés a tanulói és gondviselői adatokban.
 *
 *  Bármelyik mező megjeleníthető oszlopként és szűrhető is; a gyakori listákhoz
 *  (pl. osztálynévsor) egy kattintásos sablonok vannak. Az eredmény Excelbe vihető.
 */
@Component({
  selector: 'app-adatbank',
  templateUrl: './adatbank.component.html',
  styleUrls: ['./adatbank.component.scss'],
})
export class AdatbankComponent implements OnInit {
  sajatIntezmeny: any;

  csoportok: any[] = [];
  sablonok: any[] = [];
  osztalyok: any[] = [];

  /** A kiválasztott oszlopok kulcsai – ez a megjelenítés és az export sorrendje is. */
  valasztott: string[] = ['nev', 'oktazon', 'osztaly'];
  szurok: any[] = [];
  szoveg = '';
  osztaly = '';
  rendezes = 'nev';
  irany = 'asc';
  lezartakIs = false;
  /*  Az adatbank alapból CSAK az aktuális tanév adataira keres: a jogviszony évente új
      sort kap, ezért év-szűrő nélkül egy diák annyiszor jönne vissza, ahány tanévben itt
      volt, a lezárt évek osztályneveivel együtt. A korábbi évek külön kérhetők. */
  tanev: number = null;          // a szerver által feloldott aktuális tanév
  valasztottEv: any = '';        // '' = aktuális tanév, 'mind' = minden tanév, vagy egy évszám
  tanevek: any[] = [];
  limit = 1000;

  mezoValasztoNyitva = false;
  aktivSablon = '';

  eredmeny: any = null;
  betoltes = false;

  readonly operatorok = [
    { kulcs: 'tartalmaz', nev: 'tartalmazza' },
    { kulcs: 'egyenlo', nev: 'egyenlő' },
    { kulcs: 'nem_egyenlo', nev: 'nem egyenlő' },
    { kulcs: 'kezdodik', nev: 'ezzel kezdődik' },
    { kulcs: 'nem_ures', nev: 'ki van töltve' },
    { kulcs: 'ures', nev: 'üres' },
    { kulcs: 'nagyobb', nev: 'nagyobb mint' },
    { kulcs: 'kisebb', nev: 'kisebb mint' },
  ];

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    this.katalogusBetolt();
  }

  get intezmenyId(): string {
    return String(this.sajatIntezmeny?.intezmenyId || this.sajatIntezmeny?.id || '').trim();
  }

  katalogusBetolt() {
    if (!this.intezmenyId) return;
    this.http.get<any>(`/api/adatbank/mezok/${this.intezmenyId}`).subscribe({
      next: (r) => {
        this.csoportok = r?.csoportok || [];
        this.sablonok = r?.sablonok || [];
        this.osztalyok = r?.osztalyok || [];
        this.tanev = r?.tanev ?? null;
        this.tanevek = r?.tanevek || [];
        this.kereses();
      },
      error: (e) => this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni a mezőket.', 'error'),
    });
  }

  /** Melyik tanévre keresünk éppen (a felület fejlécében). */
  get evFelirat(): string {
    if (this.valasztottEv === 'mind') return 'minden tanév';
    const ev = Number(this.valasztottEv) || this.tanev;
    return ev ? `${ev}/${ev + 1}. tanév` : 'aktuális tanév';
  }

  evValt(ertek: any) {
    this.valasztottEv = ertek;
    // Az osztálylista is a választott tanévé (a lezárt évek osztályai üres találatot adnának).
    const ev = ertek && ertek !== 'mind' ? Number(ertek) : this.tanev;
    this.osztaly = null;
    this.http.get<any>(`/api/adatbank/mezok/${this.intezmenyId}${ev && ertek !== 'mind' ? '?ev=' + ev : ''}`)
      .subscribe({ next: (r) => { this.osztalyok = r?.osztalyok || []; this.kereses(); },
                   error: () => this.kereses() });
  }

  /** Egy mező megjelenítendő neve a kulcsa alapján. */
  mezoNev(kulcs: string): string {
    for (const cs of this.csoportok) {
      const m = (cs.mezok || []).find((x: any) => x.kulcs === kulcs);
      if (m) return m.nev;
    }
    return kulcs;
  }

  get osszesMezo(): any[] {
    return this.csoportok.flatMap((cs) => cs.mezok || []);
  }

  vanMezo(kulcs: string): boolean {
    return this.valasztott.includes(kulcs);
  }

  mezoValt(kulcs: string, be: boolean) {
    if (be && !this.valasztott.includes(kulcs)) this.valasztott.push(kulcs);
    if (!be) this.valasztott = this.valasztott.filter((k) => k !== kulcs);
    this.aktivSablon = '';
  }

  /** Oszlopsorrend állítása – az export is ebben a sorrendben készül. */
  mozgat(i: number, irany: number) {
    const cel = i + irany;
    if (cel < 0 || cel >= this.valasztott.length) return;
    const [elem] = this.valasztott.splice(i, 1);
    this.valasztott.splice(cel, 0, elem);
  }

  ujSzuro() {
    this.szurok.push({ mezo: 'nev', operator: 'tartalmaz', ertek: '' });
  }

  torolSzuro(i: number) {
    this.szurok.splice(i, 1);
  }

  /** Sablon alkalmazása: beállítja az oszlopokat, a szűrőket és a rendezést. */
  sablonAlkalmaz(s: any) {
    this.aktivSablon = s.kulcs;
    this.valasztott = [...(s.mezok || [])];
    this.szurok = (s.szurok || []).map((x: any) => ({ ...x }));
    this.rendezes = s.rendezes || 'nev';
    this.irany = 'asc';
    if (s.osztaly_kell && !this.osztaly && this.osztalyok.length) {
      this.data.sendToast('Válassz osztályt is a listához.', 'warning');
    }
    this.kereses();
  }

  private torzs(): any {
    return {
      mezok: this.valasztott,
      szurok: this.szurok.filter((s) => s.mezo),
      szoveg: this.szoveg,
      osztaly: this.osztaly || null,
      rendezes: this.rendezes,
      irany: this.irany,
      limit: this.limit,
      lezartak_is: this.lezartakIs,
      // '' → a szerver az aktuális tanévre szűr; 'mind' → minden tanév; egyébként az adott év
      ev: this.valasztottEv && this.valasztottEv !== 'mind' ? Number(this.valasztottEv) : null,
      minden_tanev: this.valasztottEv === 'mind',
    };
  }

  kereses() {
    if (!this.intezmenyId) return;
    this.betoltes = true;
    this.http.post<any>(`/api/adatbank/kereses/${this.intezmenyId}`, this.torzs()).subscribe({
      next: (r) => { this.eredmeny = r; this.betoltes = false; },
      error: (e) => {
        this.betoltes = false;
        this.data.sendToast(e?.error?.error || 'A keresés nem sikerült.', 'error');
      },
    });
  }

  /** Az aktuális találati lista Excelben – ugyanazok az oszlopok, ugyanaz a sorrend. */
  export() {
    const osztalyNev = this.osztalyok.find((o) => o.id === this.osztaly)?.osztaly;
    const nev = this.aktivSablon
      ? `${this.sablonok.find((s) => s.kulcs === this.aktivSablon)?.nev || 'adatbank'}`
      : 'adatbank';

    this.http.post(`/api/adatbank/export/${this.intezmenyId}`,
      { ...this.torzs(), fajl_nev: `${nev}${osztalyNev ? '-' + osztalyNev : ''}`, lap_nev: nev },
      { responseType: 'blob' }).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${nev}${osztalyNev ? '-' + osztalyNev : ''}.xlsx`.replace(/[^\w.\-À-ſ]+/g, '_');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: () => this.data.sendToast('Az export nem sikerült.', 'error'),
    });
  }
}
